<script setup lang="ts">
import "./MItem.scss";
import { computed } from "vue";
import { usePermission } from "../../../composables/permission";
import MTooltipBasic from "../MTooltipBasic";
import { useRouter } from "vue-router";
import type { MItemProps } from "./MItem.props";
import { mItemDefaults } from "./MItem.props";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<MItemProps>(), mItemDefaults);

const { hasAccess, disabled, onClick } = usePermission(props, false, useRouter());

// Rows are clickable by default (legacy q-item behaviour: clickable unless
// noClickable / disabled). The `clickable` prop is accepted but, as before,
// only its absence-via-noClickable matters.
const isClickable = computed(() => !props.noClickable && !disabled.value);

const classes = computed(() => [
  "m-item",
  isClickable.value && "m-item--clickable",
  props.dense && "m-item--dense",
  props.focused && "m-item--focused",
  props.active && "m-item--active",
  disabled.value && "m-item--disabled",
]);

// Render a real router-link for `to` navigation (q-item used to do this
// internally); a plain element otherwise. Disabled rows never navigate.
const tag = computed(() => (props.to && !disabled.value ? "router-link" : "div"));

function onKeydown(ev: KeyboardEvent) {
  // Plain (non-link) clickable rows need keyboard activation; router-link
  // handles Enter natively.
  if (tag.value === "router-link" || !isClickable.value) return;
  if (ev.key === "Enter" || ev.key === " ") {
    ev.preventDefault();
    onClick(ev);
  }
}
</script>

<template>
  <component
    :is="tag"
    :class="classes"
    :to="tag === 'router-link' ? to : undefined"
    role="listitem"
    :tabindex="isClickable ? 0 : undefined"
    :aria-disabled="disabled"
    @click="onClick"
    @keydown="onKeydown"
    v-bind="$attrs"
  >
    <MTooltipBasic v-if="$props.disable || $props.disabled">{{ disabledTooltip }}</MTooltipBasic>
    <MTooltipBasic v-else-if="!hasAccess">
      You don’t have permission to perform this action.
    </MTooltipBasic>
    <slot />
  </component>
</template>
